import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronRight, CheckCircle2, BookOpen, Play } from 'lucide-react';
import { API_URL as API } from '@/utils/apiConfig';

import { useTranslation } from 'react-i18next';

/**
 * Today's Reading at a Glance.
 *
 * Shows the user's active reading plans as a horizontal snap-scroll
 * carousel. When the user only has one active plan we render the
 * single card exactly as before — no dots, no swipe hint. When they
 * have two or more, each plan becomes a full-width slide with
 * pagination dots underneath, so the user can browse across all
 * their in-flight plans from a single home-screen surface instead
 * of scrolling to a secondary "Continue your plan" rail.
 *
 * Multi-passage support (per slide):
 *   When a day has multiple passages (Bible-in-a-Year days mix
 *   OT + NT), each passage shows a tiny check chip. If at least one
 *   passage is unread, the card surfaces a "Resume with <next>" mini
 *   CTA that opens the Bible reader scoped to that passage.
 */
const CACHE_KEY = 'home_cache_today_plans';

const readCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || 'null'); }
  catch { return null; }
};

export default function TodayReadingCard({ disabled = false }) {
  const { t, i18n } = useTranslation();
  // Stale-while-revalidate: null = never fetched (show skeleton).
  const [plans, setPlans] = useState(readCache);
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef(null);
  const lang = (i18n.language || 'en').split('-')[0];

  useEffect(() => {
    if (disabled) return undefined;
    let cancelled = false;
    axios.get(`${API}/reading-plans/me/today`, {
      params: { locale: lang },
      withCredentials: true,
    })
      .then((res) => {
        if (cancelled) return;
        const next = res.data?.plans || [];
        setPlans(next);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch { /* quota */ }
      })
      .catch(() => { if (!cancelled) setPlans((p) => p || []); });
    return () => { cancelled = true; };
  }, [disabled, lang]);

  // Track the visible slide index by watching scroll position. Uses
  // `scrollLeft / clientWidth` rather than IntersectionObserver so we
  // stay accurate even when the user flicks between slides quickly.
  const count = plans?.length || 0;
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || count <= 1) return undefined;
    const onScroll = () => {
      const w = el.clientWidth || 1;
      const idx = Math.round(el.scrollLeft / w);
      setActiveIdx((prev) => (prev === idx ? prev : idx));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [count]);

  if (disabled) return null;
  if (plans === null) return <TodayReadingSkeleton />;
  if (count === 0) return null;

  return (
    <div className="mb-6" data-testid="todays-reading-carousel">
      <div
        ref={scrollerRef}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4 gap-4"
        style={{ scrollbarWidth: 'none' }}
      >
        {plans.map((entry) => (
          <div
            key={entry.plan.plan_id}
            className="snap-center shrink-0 w-full"
            data-testid={`todays-reading-slide-${entry.plan.plan_id}`}
          >
            <PlanSlide entry={entry} t={t} />
          </div>
        ))}
      </div>
      {plans.length > 1 && (
        <div
          className="mt-3 flex items-center justify-center gap-1.5"
          data-testid="todays-reading-pagination"
          aria-label="Study plan pages"
        >
          {plans.map((entry, i) => (
            <button
              key={entry.plan.plan_id}
              type="button"
              onClick={() => {
                const el = scrollerRef.current;
                if (!el) return;
                el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
              }}
              aria-label={`Go to plan ${i + 1} of ${plans.length}`}
              className="transition-all"
              style={{
                width: i === activeIdx ? 18 : 6,
                height: 6,
                borderRadius: 999,
                background:
                  i === activeIdx
                    ? 'var(--theme-accent)'
                    : 'var(--glass-border, rgba(255,255,255,0.2))',
              }}
              data-testid={`todays-reading-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// Mirrors PlanSlide's footprint so the real card swaps in without a jump.
function TodayReadingSkeleton() {
  const bar = (w, h = 10) => (
    <div className="rounded-full animate-pulse" style={{ width: w, height: h, background: 'var(--glass-border, rgba(255,255,255,0.08))' }} />
  );
  return (
    <div className="mb-6" data-testid="todays-reading-skeleton" aria-hidden="true">
      <div
        className="rounded-2xl p-5 space-y-3"
        style={{ background: 'var(--theme-card)', border: '1px solid var(--glass-border, rgba(255,255,255,0.08))' }}
      >
        {bar('45%')}
        <div className="pt-1 space-y-2">{bar('30%')}{bar('65%', 16)}</div>
        {bar('40%')}
        <div className="pt-2 space-y-2">{bar('100%', 4)}{bar('35%')}</div>
      </div>
    </div>
  );
}

/**
 * A single plan slide inside the carousel. Pure presentational — all
 * data comes in via the `entry` prop from `/reading-plans/me/today`.
 */
function PlanSlide({ entry, t }) {
  const navigate = useNavigate();
  const { plan, day, progress } = entry;
  const labels = day.passage_labels || [];
  const readIdxs = new Set(day.read_passage_idxs || []);
  const allRead = labels.length > 0 && readIdxs.size === labels.length;
  const hasResumePassage =
    !progress.is_today_completed && day.next_passage_idx !== null && day.next_passage_idx !== undefined;

  const handleResume = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!day.next_passage_ref) return;
    navigate('/bible', { state: { targetReference: day.next_passage_ref } });
  };

  return (
    <button
      type="button"
      onClick={() => navigate(`/study-plans/${plan.plan_id}/day/${day.day_number}`)}
      data-testid="todays-reading-card"
      className="group relative w-full text-left rounded-2xl p-5 transition-all hover:translate-y-[-1px] hover:shadow-lg"
      style={{
        background: 'var(--theme-card)',
        border: `1px solid var(--glass-border, rgba(255,255,255,0.08))`,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--theme-textMuted)' }}>
          <Calendar className="w-3.5 h-3.5" />
          <span>{t('generated.todayreadingcard_today_s_reading_096f')}</span>
          <span className="opacity-50">•</span>
          <span>{t('generated.todayreadingcard_day_486d')} {day.day_number} of {progress.total_days}</span>
          {progress.is_today_completed && (
            <>
              <span className="opacity-50">•</span>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-emerald-500">{t('generated.todayreadingcard_completed_492e')}</span>
            </>
          )}
        </div>
        <ChevronRight
          className={`w-5 h-5 mt-0.5 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition ${
            !progress.is_today_completed ? 'animate-pulse-subtle' : ''
          }`}
          style={{ color: 'var(--theme-text)' }}
        />
      </div>
      <div className="mt-3">
        <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--theme-accent)' }}>
          {plan.title}
        </p>
        <h3 className="text-base font-semibold leading-snug" style={{ color: 'var(--theme-text)' }}>
          {day.title || `Day ${day.day_number}`}
        </h3>

        {labels.length > 1 ? (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5" data-testid="todays-reading-passages">
            <BookOpen className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--theme-textMuted)' }} />
            {labels.map((lbl, i) => {
              const isRead = readIdxs.has(i);
              return (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full"
                  style={{
                    background: isRead ? 'color-mix(in srgb, var(--theme-accent) 14%, transparent)' : 'var(--glass-bg)',
                    color: isRead ? 'var(--theme-accent)' : 'var(--theme-textSecondary)',
                    border: `1px solid ${isRead ? 'var(--theme-accent)' : 'var(--glass-border, rgba(255,255,255,0.08))'}`,
                    textDecoration: isRead ? 'line-through' : 'none',
                  }}
                  data-testid={`todays-passage-chip-${i}`}
                >
                  {isRead && <CheckCircle2 className="w-3 h-3" />}
                  {lbl}
                </span>
              );
            })}
          </div>
        ) : (
          <div className="mt-2 flex items-center flex-wrap gap-2 text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
            <BookOpen className="w-3.5 h-3.5" style={{ color: 'var(--theme-textMuted)' }} />
            <span>{labels[0]}</span>
          </div>
        )}

        {day.has_rich_content && day.intro_snippet && (
          <p className="mt-3 text-sm leading-relaxed line-clamp-3" style={{ color: 'var(--theme-textSecondary)' }}>
            {day.intro_snippet}
          </p>
        )}
      </div>
      {hasResumePassage && readIdxs.size > 0 && !allRead && (
        <div
          role="link"
          tabIndex={0}
          onClick={handleResume}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleResume(e); }}
          className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-all hover:scale-[1.03] active:scale-[0.98] cursor-pointer"
          style={{
            background: 'var(--theme-accent)',
            color: '#fff',
          }}
          data-testid="todays-reading-resume-btn"
        >
          <Play className="w-3 h-3" />
          {t('generated.todayreadingcard_continue_with_4cea')} {day.next_passage_label}
        </div>
      )}
      <div className="mt-4">
        <div className="h-1 w-full rounded-full" style={{ background: 'var(--glass-border, rgba(255,255,255,0.06))' }}>
          <div
            className="h-1 rounded-full transition-all"
            style={{
              width: `${progress.completion_pct}%`,
              background: 'var(--theme-accent)',
            }}
          />
        </div>
        <p className="mt-1.5 text-[11px] flex items-center justify-between gap-2" style={{ color: 'var(--theme-textMuted)' }}>
          <span>{progress.completed_days_count} of {progress.total_days} {t('generated.todayreadingcard_days_complete_7e5d')}{progress.completion_pct}%)</span>
          {!progress.is_today_completed && readIdxs.size === 0 && (
            <span style={{ color: 'var(--theme-accent)' }}>{t('generated.todayreadingcard_pick_up_where_fec7')}</span>
          )}
        </p>
      </div>
    </button>
  );
}
